import React from 'react'

import { Badge } from '../Badge'
import { SECURITY_SCHEMA as txt } from '../BadgeContent'
import { ProgressBar, ProgressBarText } from '../ProgressBar'

import { useHostCustomer } from '../../../../hooks/useHostCustomer'
import { useAlertsApi } from '../../../../api/useAlertsApi'
import { useSecurityTasks } from '../../../../api/useSecurityTasks'

import {
  getProgressText,
  getSecurityType,
  isOnlineStatement,
} from '../utils/security'
import { diff, mergeLoadStates, normalizeQueryStatus } from '../utils/common'

function SecurityBadge(): JSX.Element {
  const {
    isAfg,
    afgCustomer,
    hostLoading,
    legacyCustomer,
    passwordStrengthIndicator,
  } = useHostCustomer()
  const { data: clickData, status: clickStatus } = useSecurityTasks() // badges/security
  const { data: aData, status: aStatus, isSuccess: aSuccess } = useAlertsApi() // alerts?category=custom
  const secLoading = normalizeQueryStatus(clickStatus)
  const alertsLoading = normalizeQueryStatus(aStatus)
  const combinedLoading = mergeLoadStates(
    secLoading,
    hostLoading,
    alertsLoading,
  )

  // Determine if each user task is completed
  const hasStrongPw = passwordStrengthIndicator === '3'
  const hasPaperless = isOnlineStatement(isAfg, legacyCustomer, afgCustomer)
  const hasAlerts = aSuccess && aData?.alerts && aData?.alerts?.length >= 1
  const userTasks = [
    {
      sortKey: 'PASSWORD',
      isCompleted: hasStrongPw, // psi is either 1, 2 or 3 -> '3' is a strong password
    },
    {
      sortKey: 'PAPERLESS',
      isCompleted: hasPaperless, // 'Online' = true
    },
    {
      sortKey: 'ALERTS',
      isCompleted: hasAlerts ?? false, // if user has customized any alerts = true
    },
  ]
  const clickTasks = clickStatus === 'success' ? clickData : []
  const allTasks = [...userTasks, ...clickTasks]

  // Progress Bar Text & completion State
  const total = allTasks.length
  const currProgress = allTasks.filter(t => t.isCompleted === true).length
  const endowment = 1 // UX Team Request -> Endowment effect - they never start from 0
  const actual = currProgress + endowment
  const tier1Cutoff = 3
  const tier1Remainder = diff(total - tier1Cutoff - currProgress)
  const tier2Remainder = diff(total - currProgress)
  const secType = getSecurityType(currProgress)
  const progressText = getProgressText(secType, tier1Remainder, tier2Remainder)

  return (
    <Badge
      status={combinedLoading}
      badgeType="progress"
      icon={txt.icon[secType]}
      heading={txt.heading[secType]}
      desc={txt.desc[secType]}
      progressBar={<ProgressBar actual={actual} total={total + endowment} />}
      progressText={<ProgressBarText>{progressText}</ProgressBarText>}
    />
  )
}

export { SecurityBadge }
